import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FaClipboardList,
  FaPlusCircle,
  FaFileAlt,
  FaEnvelopeOpenText,
  FaUserShield,
  FaChartBar,
  FaShieldAlt,
  FaBolt,
  FaTag,
  FaServer,
  FaWpforms,
  FaBalanceScale,
  FaUserCog,
} from 'react-icons/fa';
import { useRoleAccess } from '@/contexts/RoleAccessContext';
import { SolutionKey } from '@/security/solutionAccess';
import './AdminHome.scss';

interface AdminTile {
  title: string;
  description: string;
  to: string;
  icon: React.ReactNode;
  solution: SolutionKey;
}

const tiles: AdminTile[] = [
  {
    title: 'All Reviews',
    description: 'View and manage all active and completed reviews.',
    to: '/admin/reviews',
    icon: <FaClipboardList />,
    solution: 'reviews',
  },
  {
    title: 'Assign Review',
    description: 'Assign a review template to a user.',
    to: '/admin/assign-review',
    icon: <FaPlusCircle />,
    solution: 'reviews',
  },
  {
    title: 'Review Templates',
    description: 'Create and edit review templates and steps.',
    to: '/admin/reviews/templates/new',
    icon: <FaFileAlt />,
    solution: 'reviews',
  },
  {
    title: 'Email Approvals',
    description: 'Manage pending email-based approval requests.',
    to: '/admin/email-approval',
    icon: <FaEnvelopeOpenText />,
    solution: 'emailApprovals',
  },
  {
    title: 'Role Management',
    description: 'Assign and revoke application roles for users.',
    to: '/admin/roles',
    icon: <FaUserShield />,
    solution: 'roleManagement',
  },
  {
    title: 'Reports',
    description: 'View admin reports and usage data.',
    to: '/admin/reports',
    icon: <FaChartBar />,
    solution: 'reports',
  },
  {
    title: 'PIM',
    description: 'Privileged Identity Management — manage elevated access.',
    to: '/admin/pim',
    icon: <FaShieldAlt />,
    solution: 'pim',
  },
  {
    title: 'Automated Elevations',
    description: 'Manage PIM automated elevation group membership.',
    to: '/admin/pim/automated-elevations',
    icon: <FaBolt />,
    solution: 'automatedElevations',
  },
  {
    title: 'Employee Offers',
    description: 'Review sync queue, manage active offers, and approve contractors.',
    to: '/admin/employee-offers',
    icon: <FaTag />,
    solution: 'employeeOffers',
  },
  {
    title: 'Groups Admin',
    description: 'View SharePoint site storage usage and trigger archive reviews.',
    to: '/admin/groups',
    icon: <FaServer />,
    solution: 'groupsAdmin',
  },
  {
    title: 'OOB Status Board',
    description: 'Manage out-of-band readiness, events, updates, and acknowledgements.',
    to: '/admin/oob',
    icon: <FaShieldAlt />,
    solution: 'outOfBand',
  },
  {
    title: 'Forms',
    description: 'Create and manage form definitions, sections, fields, and submissions.',
    to: '/admin/forms',
    icon: <FaWpforms />,
    solution: 'forms',
  },
  {
    title: 'RC Questions',
    description: 'Review and manage Resource Health help questions and answers.',
    to: '/admin/resource-compliance-questions',
    icon: <FaBalanceScale />,
    solution: 'forms',
  },
  {
    title: 'User Admin',
    description: 'Search user directories and manage user access cleanup operations.',
    to: '/admin/users',
    icon: <FaUserCog />,
    solution: 'userAdmin',
  },
];

const AdminHome: React.FC = () => {
  const { loading, canAccessSolution } = useRoleAccess();
  const visibleTiles = tiles.filter((tile) => canAccessSolution(tile.solution));

  if (loading) {
    return <Container className="mt-4 admin-home"><p>Loading permissions...</p></Container>;
  }

  return (
    <Container className="mt-4 admin-home">
      <div className="mb-4">
        <h2 className="mb-1">Admin Panel</h2>
        <p className="text-muted">Select a section to manage.</p>
      </div>

      <Row className="g-3">
        {visibleTiles.map((tile) => (
          <Col key={tile.to} xs={6} sm={4} md={3}>
            <Card
              as={Link}
              to={tile.to}
              className="admin-home-tile h-100 text-decoration-none"
            >
              <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center p-3 gap-2">
                <div className="admin-home-tile-icon">{tile.icon}</div>
                <div className="fw-semibold">{tile.title}</div>
                <div className="admin-home-tile-desc small">{tile.description}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default AdminHome;
